import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { CONTACT } from "../../data/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/brands", label: "Bath Fittings" },
  { to: "/pipes-ro", label: "Pipes & RO" },
  { to: "/tile-adhesives", label: "Tile Adhesives" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Logo = ({ className = "", white = false }) => (
  <img
    src={white ? "/logo-white.webp" : "/logo.webp"}
    alt="Shri Radhey Krishna Bath & Tiles"
    className={`w-auto ${className}`}
  />
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className="fixed inset-x-0 top-0 z-50 border-b border-brand/20 bg-paper/95 shadow-[0_10px_40px_-24px_rgba(22,32,44,0.35)] backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 sm:px-8 lg:px-16">
        <Link to="/" data-testid="navbar-logo" className="group leading-none">
          <Logo className="h-16 sm:h-20" />
        </Link>

        <nav className="hidden items-center gap-5 lg:flex xl:gap-7" data-testid="navbar-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "text-brand" : "text-[#5C6570] hover:text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <a
            href={CONTACT.phoneHref}
            data-testid="navbar-phone-button"
            className="flex items-center gap-2 font-mono text-xs tracking-wider text-[#5C6570] transition-colors hover:text-brand"
          >
            <Phone size={14} /> {CONTACT.phoneDisplay}
          </a>
          <Link
            to="/contact"
            data-testid="navbar-enquire-button"
            className="border border-brand/60 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-brand transition-all duration-300 hover:bg-brand hover:text-paper"
          >
            Enquire Now
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="text-ink lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="navbar-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.215, 0.61, 0.355, 1] }}
            className="overflow-hidden border-t border-brand/15 bg-paper lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-3 font-serif text-2xl ${isActive ? "text-brand" : "text-ink"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-enquire-button"
                className="mt-4 border border-brand/60 px-5 py-3.5 text-center font-mono text-xs uppercase tracking-[0.2em] text-brand"
              >
                Enquire Now
              </Link>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <a
                  href={CONTACT.phoneHref}
                  data-testid="mobile-nav-call-button"
                  className="flex items-center justify-center gap-2 bg-brand px-5 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-paper"
                >
                  <Phone size={14} /> Call Now
                </a>
                <a
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-whatsapp-button"
                  className="flex items-center justify-center gap-2 border border-[#25D366] px-5 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-[#1DA851]"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
